import { Color, Spacing } from '@persona-id/design-tokens';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import styled from 'styled-components';

import JsonSchemaSelect, { Option } from 'dashboard/components/JsonSchemaSelect';
import { GraphNodeConfigFormData } from 'dashboard/forms/FormGraphNodeConfiguration/FormGraphNodeConfiguration';
import { AccountType } from 'dashboard/models/account-type';
import {
  getSourceName,
  NodeFunction,
  NodeFunctionsSource,
  ParamMapping,
  ServerPresetSummary,
} from 'dashboard/models/graph-backend';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text/Text';
import {
  convertFieldSchemasToJsonSchema,
  FieldSchema,
  FieldSchemaHash,
  FieldSchemaType,
} from 'lib/fields';
import { JsonSchema, JsonSchemaTypes } from 'lib/json-schema';

interface SourceFunctionMappingProps {
  watch: UseFormWatch<GraphNodeConfigFormData>;
  setValue: UseFormSetValue<GraphNodeConfigFormData>;
  source: NodeFunctionsSource;
  nodeFunctionToken: string;
  accountType: AccountType;
  presetSummary: ServerPresetSummary;
  presetSummaries: ServerPresetSummary[];
  disabled: boolean;
  sourceIdx: number;
}

const LabelWrapper = styled.div`
  border-bottom: 1px solid ${Color.BorderHairline};
`;

const ParamMappingsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: ${Spacing[4]};
  row-gap: ${Spacing[1]};
`;

export const getFieldSchemas = (
  source: NodeFunctionsSource,
  accountType: AccountType,
): FieldSchema[] => {
  // TODO(unzi): Include output schema from previous functions
  let fieldSchemas = accountType.attributes['field-schemas'];
  if (source.path) {
    const nestedSchema = fieldSchemas.find((schema) => schema.key === source.path);
    // NOTE: Currently nested schemas are used to expose the item schema of arrays
    if (nestedSchema?.type === FieldSchemaType.Array) {
      if (nestedSchema.config['item-schema'].type === FieldSchemaType.Hash) {
        fieldSchemas = nestedSchema.config['item-schema'].config['item-schemas'];
      } else {
        fieldSchemas = [nestedSchema.config['item-schema']];
      }
    }
  }
  return fieldSchemas;
};

const SourceFunctionMapping = ({
  watch,
  setValue,
  source,
  nodeFunctionToken,
  accountType,
  presetSummary,
  presetSummaries,
  disabled,
  sourceIdx,
}: SourceFunctionMappingProps) => {
  const functionMappingIdx = source['function-mappings'].findIndex(
    (fm) => fm.token === nodeFunctionToken,
  );
  const paramMappings: ParamMapping[] = watch(
    `nodeFunctionsConfig.sources.${sourceIdx}.function-mappings.${functionMappingIdx}.param-mappings`,
  );
  const nodeFunctions: NodeFunction[] = watch('nodeFunctionsConfig.functions');

  const allPreviousFunctionOutputSchemas = useMemo((): FieldSchema[] => {
    const functionIndex = nodeFunctions.findIndex((nf) => nf.token === nodeFunctionToken);
    const allPreviousFunctions = nodeFunctions
      .slice(0, functionIndex)
      .filter((nf) => !!source['function-mappings'].find((fm) => fm.token === nf.token));

    return allPreviousFunctions.map((nf) => {
      const outputSchema = presetSummaries.find((ps) => ps.preset === nf.preset)?.['output-schema'];
      const actualIndex = nodeFunctions.findIndex((x) => x.token === nf.token);

      return {
        ...outputSchema,
        key: `function_${actualIndex + 1}_${nf.preset}`,
      } as FieldSchema;
    });
  }, [nodeFunctionToken, nodeFunctions, presetSummaries, source]);

  const fieldSchemas = useMemo(() => getFieldSchemas(source, accountType), [source, accountType]);
  const sourceFieldSchema = useMemo((): FieldSchemaHash => {
    return {
      key: 'source',
      config: {
        'item-schemas': fieldSchemas,
      },
      type: FieldSchemaType.Hash,
      label: '',
    };
  }, [fieldSchemas]);
  const jsonSchema = useMemo(
    () => convertFieldSchemasToJsonSchema([sourceFieldSchema, ...allPreviousFunctionOutputSchemas]),
    [sourceFieldSchema, allPreviousFunctionOutputSchemas],
  );
  const headerText = `Account source: ${getSourceName(source, accountType)}`;

  const handleUpdate = useCallback(
    (value: string, idx: number) => {
      let functionChainInputToken = undefined;

      if (value.startsWith('function_')) {
        const str = value.replace('function_', '');
        const functionIndex = parseInt(str.split('_')[0]) - 1;
        const nodeFunction = nodeFunctions[functionIndex];

        functionChainInputToken = nodeFunction.token;
      }

      setValue(
        `nodeFunctionsConfig.sources.${sourceIdx}.function-mappings.${functionMappingIdx}.param-mappings.${idx}`,
        {
          ...paramMappings[idx],
          path: value,
          'function-chain-input-token': functionChainInputToken,
        },
      );
    },
    [setValue, sourceIdx, functionMappingIdx, paramMappings, nodeFunctions],
  );

  const changeMappingDirectValue = useCallback(
    (value: string, idx: number) => {
      setValue(
        `nodeFunctionsConfig.sources.${sourceIdx}.function-mappings.${functionMappingIdx}.param-mappings.${idx}`,
        {
          ...paramMappings[idx],
          'direct-value': value,
        },
      );
    },
    [functionMappingIdx, paramMappings, setValue, sourceIdx],
  );

  return (
    <>
      <LabelWrapper>
        <Label size='lg' weight='bold'>
          {headerText}
        </Label>
      </LabelWrapper>
      <Spacer height={2} />
      <ParamMappingsContainer>
        {paramMappings.map((paramMapping, idx) => {
          const paramFieldSchema = presetSummary['param-schema'].find(
            (fs) => fs.key === paramMapping.destination,
          );
          const isDirectParam = presetSummary['direct-params'].includes(paramMapping.destination);
          return (
            paramFieldSchema &&
            (isDirectParam ? (
              <DirectFunctionParamMapping
                key={paramMapping.destination}
                value={paramMapping['direct-value'] || ''}
                onUpdate={changeMappingDirectValue}
                label={paramFieldSchema.label || paramMapping.destination}
                disabled={disabled}
                idx={idx}
              />
            ) : (
              <FunctionParamMapping
                key={paramMapping.destination}
                label={paramFieldSchema.label || paramMapping.destination}
                allowedTypes={paramFieldSchema.type ? [paramFieldSchema.type] : []}
                jsonSchema={jsonSchema}
                mapping={paramMapping}
                onUpdate={handleUpdate}
                disabled={disabled}
                required={paramFieldSchema.config.required}
                idx={idx}
              />
            ))
          );
        })}
      </ParamMappingsContainer>
    </>
  );
};

interface DirectFunctionParamMappingProps {
  value: string;
  onUpdate: (value: string, idx: number) => void;
  label: string;
  disabled: boolean;
  idx: number;
}

const DirectFunctionParamMapping = ({
  value,
  onUpdate,
  label,
  disabled,
  idx,
}: DirectFunctionParamMappingProps) => {
  return (
    <div>
      <Label size='md'>{label}</Label>
      <Input
        value={value}
        onChange={(e) => {
          onUpdate(e.target.value, idx);
        }}
        disabled={disabled}
      />
      <Spacer height={2} />
    </div>
  );
};

interface FunctionParamMappingProps {
  label: string;
  jsonSchema: JsonSchema;
  mapping: ParamMapping;
  allowedTypes: JsonSchemaTypes[];
  onUpdate: (value: string, idx: number) => void;
  disabled: boolean;
  required: boolean;
  idx: number;
}

const FunctionParamMapping = ({
  label,
  jsonSchema,
  mapping,
  onUpdate,
  allowedTypes,
  disabled,
  required,
  idx,
}: FunctionParamMappingProps) => {
  const [inputValue, setInputValue] = useState<string | string[]>(mapping.path);

  const handleValueChange = useCallback(
    (value: string | string[]) => {
      setInputValue(value);
      if (!value && value !== mapping.path) {
        onUpdate(value, idx);
      }
    },
    [idx, mapping.path, onUpdate],
  );

  useEffect(() => {
    handleValueChange(mapping?.path);
  }, [mapping?.path, handleValueChange]);

  const handleSelectField = (data: Option | Option[]) => {
    if (typeof data === 'object') {
      onUpdate((data as Option).value, idx);
    }
  };

  return (
    <div>
      <Label size='md'>{label}</Label>
      <JsonSchemaSelect
        allowedTypes={allowedTypes}
        jsonSchema={jsonSchema}
        inputValue={inputValue}
        tokensToDisplayValue={{}}
        onFieldSelect={handleSelectField}
        onValueChange={handleValueChange}
        disabled={disabled}
        required={required}
        closeDropdownOnBlur
      />
      <Spacer height={2} />
    </div>
  );
};

export default SourceFunctionMapping;
